import { Button } from '@lifeomic/chroma-react/components/Button';
import { defineMessages, useIntl } from 'react-intl';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { Trash } from '@lifeomic/chromicons';
import { useMutation } from '@tanstack/react-query';
import { useNotify } from '../../hooks/useNotify';
import * as React from 'react';
import FileService, {
  GetFileResponse,
  useSubjectRestrictedOptions,
} from '../../services/FileService';
import useServiceAuth from '../../hooks/useServiceAuth';

const fileService = new FileService();

const messages = defineMessages({
  tooltipTitle: {
    id: 'phc-web-toolkit.fileManagementControls.fileDeleteAction.tooltip',
    defaultMessage: 'Delete',
  },
  modalTitle: {
    id: 'phc-web-toolkit.fileManagementControls.fileDeleteAction.modal.title',
    defaultMessage: 'Delete File',
  },
  modalCancel: {
    id: 'phc-web-toolkit.fileManagementControls.fileDeleteAction.modal.cancel',
    defaultMessage: 'Cancel',
  },
  modalDelete: {
    id: 'phc-web-toolkit.fileManagementControls.fileDeleteAction.modal.delete',
    defaultMessage: 'Delete',
  },
  modalBlurb: {
    id: 'phc-web-toolkit.fileManagementControls.fileDeleteAction.modal.blurb',
    defaultMessage:
      'Are you sure you want to delete file "{name}"? This action cannot be undone.',
  },
  fileId: {
    id: 'phc-web-toolkit.fileManagementControls.fileDeleteAction.modal.fileId',
    defaultMessage: 'File: {id}',
  },
  deleteFailure: {
    id: 'phc-web-toolkit.fileManagementControls.fileDeleteAction.deleteFailure',
    defaultMessage: 'Failed to delete file.',
  },
});

interface DeleteFileModalProps {
  file: GetFileResponse;
  isOpen?: boolean;
  onDismiss?: () => any;
  onSuccess?: (file: GetFileResponse) => void;
}

const DeleteFileModal: React.FC<DeleteFileModalProps> = ({
  file,
  isOpen,
  onDismiss,
  onSuccess,
}) => {
  const { formatMessage } = useIntl();

  const auth = useServiceAuth();
  const [addNotification] = useNotify();
  const cohortRestrictionOptions = useSubjectRestrictedOptions();

  const { mutateAsync } = useMutation(
    async (fileId: string) =>
      await fileService.deleteFile(auth)(fileId, cohortRestrictionOptions),
    {
      onSuccess: () => {
        // TODO: If we end up using react-query for the main
        // files UI, we should also invalidate the cache for this
        // https://react-query.tanstack.com/docs/guides/updates-from-mutation-responses;
        return onSuccess?.(file);
      },
      onError: () => {
        addNotification(formatMessage(messages.deleteFailure), 'error');
      },
    },
  );

  const handleDelete = async () => {
    await mutateAsync(file?.id);
    onDismiss?.();
  };

  return (
    <Modal
      title={formatMessage(messages.modalTitle)}
      isOpen={isOpen}
      onDismiss={onDismiss}
      actions={
        <>
          <Button variant="text" onClick={() => onDismiss?.()}>
            {formatMessage(messages.modalCancel)}
          </Button>
          <Button onClick={() => handleDelete()}>
            {formatMessage(messages.modalDelete)}
          </Button>
        </>
      }
    >
      <Text size="subbody">
        {formatMessage(messages.modalBlurb, {
          name: file?.name,
        })}
      </Text>
      <Text size="caption">
        {formatMessage(messages.fileId, {
          id: file?.id,
        })}
      </Text>
    </Modal>
  );
};

interface FileDeleteActionProps {
  file: GetFileResponse;
  onDelete?: (file: GetFileResponse) => void;
}

export const FileDeleteAction: React.FC<FileDeleteActionProps> = ({
  file,
  onDelete,
}) => {
  const { formatMessage } = useIntl();

  const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false);

  return (
    <>
      <Tooltip title={formatMessage(messages.tooltipTitle)} placement="bottom">
        <IconButton
          aria-label={formatMessage(messages.tooltipTitle)}
          aria-haspopup="dialog"
          aria-expanded={isDialogOpen}
          icon={Trash}
          onClick={e => {
            e.stopPropagation();
            e.currentTarget.blur();
            setIsDialogOpen(true);
          }}
          color="inverse"
        />
      </Tooltip>
      {isDialogOpen && (
        <DeleteFileModal
          file={file}
          isOpen={isDialogOpen}
          onDismiss={() => {
            setIsDialogOpen(false);
          }}
          onSuccess={deletedFile => {
            onDelete?.(deletedFile);
          }}
        />
      )}
    </>
  );
};
